import type { Employee, EmployeeStatus, UpdateEmployeeRequest } from '@coworker/contracts';
import {
  EMPLOYEE_JOB_DESCRIPTION_MAX,
  EMPLOYEE_NAME_MAX,
  EMPLOYEE_ROLE_TITLE_MAX,
} from '@coworker/contracts';
import { useState, type FormEvent } from 'react';

import { Badge, type BadgeProps } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { useCurrentWorkspace } from '@/hooks/use-current-workspace.js';
import {
  useCreateEmployee,
  useEmployees,
  useHireEmployee,
  useUpdateEmployee,
} from '@/hooks/use-employees.js';

/**
 * The workspace's employees - EMP-006.
 *
 * The first screen in this product that renders data a person created rather
 * than a placeholder naming the ticket that owns it. The Generalist has existed
 * in the database since AUTH-010 and nobody has ever seen it.
 *
 * One lifecycle action, and only one: hiring a draft. The rest of the lifecycle
 * needs the transition table, which lives in `packages/domain` where ADR-0022
 * keeps this project from reaching it - see `useHireEmployee`. EMP-007 owns the
 * profile editor and the rest of the verbs.
 */
const STATUS_VARIANT: Readonly<Record<EmployeeStatus, BadgeProps['variant']>> = {
  DRAFT: 'muted',
  PROBATION: 'outline',
  ACTIVE: 'default',
  PAUSED: 'outline',
  RETIRED: 'muted',
};

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : 'Something went wrong';
}

export function Employees() {
  const workspace = useCurrentWorkspace();
  const { query } = useEmployees();
  const create = useCreateEmployee();
  const hire = useHireEmployee();
  const update = useUpdateEmployee();

  /**
   * Which rows have a request in flight.
   *
   * **A set, not the mutation's own `isPending`.** One boolean shared by every
   * card disables every other row's buttons while one of them is working, and
   * `Integrations.tsx` records what that cost twice over there before it was a
   * set. With a second per-row action on this screen it stops being cosmetic.
   */
  const [busy, setBusy] = useState<ReadonlySet<string>>(() => new Set());

  /**
   * Runs a per-row mutation and answers whether it worked.
   *
   * **The boolean is load-bearing.** A first version resolved either way, so a
   * card could not tell a saved edit from a refused one and closed its form on
   * both - the person lost what they had typed and had to retype it to find out
   * whether the second attempt worked. The mutation still records its own error;
   * catching it here only keeps an already-reported path from becoming an
   * unhandled rejection.
   */
  function act(employeeId: string, run: () => Promise<unknown>): Promise<boolean> {
    setBusy((current) => new Set(current).add(employeeId));
    return run()
      .then(() => true)
      .catch(() => false)
      .finally(() => {
        setBusy((current) => {
          const next = new Set(current);
          next.delete(employeeId);
          return next;
        });
      });
  }

  const [name, setName] = useState('');
  const [roleTitle, setRoleTitle] = useState('');

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    create.mutate(
      { name: name.trim(), roleTitle: roleTitle.trim() },
      {
        onSuccess: () => {
          setName('');
          setRoleTitle('');
        },
      },
    );
  }

  return (
    <section className="mx-auto flex max-w-3xl flex-col gap-8">
      <h1 className="text-2xl font-semibold">Employees</h1>

      {workspace === undefined ? (
        // A real state, not a loading one: the session carries INVITED and
        // SUSPENDED memberships too, and neither authorizes anything.
        <p className="text-sm text-muted-foreground">
          You are not an active member of any workspace yet. Once an invitation is accepted, your
          employees appear here.
        </p>
      ) : (
        <>
          <Roster
            query={query}
            onHire={(id) => void act(id, () => hire.mutateAsync(id))}
            onSave={(id, edit) => act(id, () => update.mutateAsync({ employeeId: id, edit }))}
            busy={busy}
          />

          {hire.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {errorText(hire.error)}
            </p>
          ) : null}
          {update.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {errorText(update.error)}
            </p>
          ) : null}

          <form onSubmit={onSubmit} className="flex flex-col gap-3 border-t border-border pt-6">
            <h2 className="text-lg font-semibold">Hire a specialist</h2>
            <p className="text-sm text-muted-foreground">
              A new specialist starts as a draft. Hiring one puts it on probation, which is where it
              can start work.
            </p>

            <label className="flex flex-col gap-1 text-sm">
              Name
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                maxLength={120}
                className="rounded-[--radius] border border-border bg-transparent px-3 py-2"
              />
            </label>

            <label className="flex flex-col gap-1 text-sm">
              Role title
              <input
                value={roleTitle}
                onChange={(event) => setRoleTitle(event.target.value)}
                required
                maxLength={120}
                className="rounded-[--radius] border border-border bg-transparent px-3 py-2"
              />
            </label>

            {create.isError ? (
              <p role="alert" className="text-sm text-destructive">
                {errorText(create.error)}
              </p>
            ) : null}

            <Button type="submit" disabled={create.isPending} className="self-start">
              {create.isPending ? 'Hiring…' : 'Hire'}
            </Button>
          </form>
        </>
      )}
    </section>
  );
}

interface RosterProps {
  readonly query: ReturnType<typeof useEmployees>['query'];
  readonly onHire: (employeeId: string) => void;
  readonly onSave: (employeeId: string, edit: UpdateEmployeeRequest) => Promise<boolean>;
  readonly busy: ReadonlySet<string>;
}

function Roster({ query, onHire, onSave, busy }: RosterProps) {
  if (query.isPending) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Loading employees…
      </p>
    );
  }

  if (query.isError) {
    return (
      <div role="alert" className="flex flex-col items-start gap-3">
        <p className="text-sm text-destructive">{errorText(query.error)}</p>
        <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const { employees } = query.data;

  if (employees.length === 0) {
    // Reachable, and not only in theory: onboarding creates a Generalist, but a
    // workspace created through `POST /v1/workspaces` gets none - which is
    // exactly what the cloud suite observes ("a workspace seeded without
    // onboarding has no employees").
    return (
      <p className="text-sm text-muted-foreground">
        This workspace has no employees yet. Hire a specialist below.
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-3">
      {employees.map((employee) => (
        <EmployeeCard
          key={employee.id}
          employee={employee}
          onHire={() => onHire(employee.id)}
          onSave={(edit) => onSave(employee.id, edit)}
          busy={busy.has(employee.id)}
        />
      ))}
    </ul>
  );
}

interface EmployeeCardProps {
  readonly employee: Employee;
  readonly onHire: () => void;
  readonly onSave: (edit: UpdateEmployeeRequest) => Promise<boolean>;
  readonly busy: boolean;
}

function EmployeeCard({ employee, onHire, onSave, busy }: EmployeeCardProps) {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <li className="rounded-[--radius] border border-border p-4">
        <EmployeeEditor
          employee={employee}
          busy={busy}
          onCancel={() => setEditing(false)}
          onSave={async (edit) => {
            // Closed only on success. A refused save keeps the form and what
            // was typed in it; the screen says why above the roster.
            if (await onSave(edit)) setEditing(false);
          }}
        />
      </li>
    );
  }

  return (
    <li className="flex flex-col gap-2 rounded-[--radius] border border-border p-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-medium">{employee.name}</span>
        <span className="text-sm text-muted-foreground">{employee.roleTitle}</span>
        <Badge variant={employee.employeeType === 'GENERALIST' ? 'default' : 'outline'}>
          {employee.employeeType}
        </Badge>
        <Badge variant={STATUS_VARIANT[employee.status]}>{employee.status}</Badge>

        <div className="ml-auto flex gap-2">
          {employee.status === 'DRAFT' ? (
            <Button variant="outline" size="sm" disabled={busy} onClick={onHire}>
              {busy ? 'Hiring…' : 'Hire'}
            </Button>
          ) : null}
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            Edit
          </Button>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        {employee.jobDescription.trim() === ''
          ? 'No job description yet. Edit this employee to say what it is for.'
          : employee.jobDescription}
      </p>
    </li>
  );
}

/**
 * The profile form - EMP-007.
 *
 * **The job description is the field this screen exists for.** The agent
 * runtime builds the model's system prompt from name, role title and this, so
 * until it could be written every employee was prompt-identical apart from a
 * name. It is a textarea rather than an input because what belongs in it is a
 * paragraph about what the employee is for, not a job title again.
 *
 * The draft lives here and the server holds the committed answer: nothing is
 * sent until Save, and Cancel leaves the roster untouched. Only fields that
 * actually changed are sent - the request schema makes each one optional and
 * the repository coalesces the rest, so a body naming nothing is a no-op rather
 * than an erasure.
 */
function EmployeeEditor({
  employee,
  busy,
  onSave,
  onCancel,
}: {
  readonly employee: Employee;
  readonly busy: boolean;
  readonly onSave: (edit: UpdateEmployeeRequest) => Promise<void>;
  readonly onCancel: () => void;
}) {
  const [name, setName] = useState(employee.name);
  const [roleTitle, setRoleTitle] = useState(employee.roleTitle);
  const [jobDescription, setJobDescription] = useState(employee.jobDescription);

  const edit: UpdateEmployeeRequest = {
    ...(name.trim() === employee.name ? {} : { name: name.trim() }),
    ...(roleTitle.trim() === employee.roleTitle ? {} : { roleTitle: roleTitle.trim() }),
    ...(jobDescription.trim() === employee.jobDescription
      ? {}
      : { jobDescription: jobDescription.trim() }),
  };
  const changed = Object.keys(edit).length > 0;
  // Both are `min(1)` on the server; an empty one would be a 422 the person
  // cannot act on, so the button says no first.
  const complete = name.trim() !== '' && roleTitle.trim() !== '';

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        void onSave(edit);
      }}
    >
      <label className="flex flex-col gap-1 text-sm">
        Name
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={EMPLOYEE_NAME_MAX}
          className="rounded-[--radius] border border-border bg-transparent px-3 py-2"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        Role title
        <input
          value={roleTitle}
          onChange={(event) => setRoleTitle(event.target.value)}
          maxLength={EMPLOYEE_ROLE_TITLE_MAX}
          className="rounded-[--radius] border border-border bg-transparent px-3 py-2"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        Job description
        <span className="text-xs text-muted-foreground">
          What this employee is for. The employee is told this before every conversation, so it is
          what makes one specialist answer differently from another.
        </span>
        <textarea
          value={jobDescription}
          onChange={(event) => setJobDescription(event.target.value)}
          maxLength={EMPLOYEE_JOB_DESCRIPTION_MAX}
          rows={4}
          className="rounded-[--radius] border border-border bg-transparent px-3 py-2"
        />
      </label>

      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={busy || !changed || !complete}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
